<script lang="ts" setup>
import { computed } from 'vue';
import { Picture } from '~/types';
import Image from '~/components/ui/Image.vue';

export interface Item {
  image: Picture;
  title: string;
  description?: string;
  url?: string;
}

interface Props {
  item: Item;
}

const props = defineProps<Props>();
const wrapperTag = computed(() => (props.item.url ? 'a' : 'div'));
const attrs = computed(() => (props.item.url ? { href: props.item.url } : undefined));
</script>

<template>
  <div class="tiles-slider-item">
    <component
      :is="wrapperTag"
      v-bind="attrs"
      class="tiles-slider-item__thumb"
      :class="{ 'tiles-slider-item__thumb--link': item.url }"
    >
      <Image v-if="item.image" :image="item.image" class="tiles-slider-item__image" />
    </component>
    <div class="tiles-slider-item__body">
      <strong v-if="item.title" class="tiles-slider-item__title">
        <a v-if="item.url" :href="item.url" class="tiles-slider-item__title-link">{{ item.title }}</a>
        <template v-else>{{ item.title }}</template>
      </strong>
      <p v-if="item.description" class="tiles-slider-item__description">{{ item.description }}</p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tiles-slider-item {
  @apply w-full flex flex-col gap-5;

  &__thumb {
    @apply w-full relative rounded-10 overflow-hidden aspect-[4/3];

    &--link {
      @apply [&>img]:hover:scale-105;
    }
  }

  &__image {
    @apply absolute w-full h-full inset-0 object-cover object-center duration-300;
  }

  &__body {
    @apply w-full flex flex-col gap-4;
  }

  &__title {
    @apply font-title text-white text-[28px] leading-[1.1] uppercase;
    @apply xl:text-[38px];

    &-link {
      @apply duration-300 hover:text-gray-300;
    }
  }

  &__description {
    @apply text-white/70 text-[16px] leading-[1.3];
  }
}
</style>
